// Drop-in Settings gear icon + modal for entering a Groq API key.
// Stores key in LocalStorage via useGroqKey. Safe to mount anywhere in your
// PWA's header — usually next to the title.

import { useState, useEffect } from 'react'
import { useGroqKey } from './useGroqKey'

interface Props {
  /** Optional class for the trigger button. */
  className?: string
  /** Defaults to a small gear glyph; pass children to fully override. */
  children?: React.ReactNode
}

export function SettingsButton({ className, children }: Props) {
  const [open, setOpen] = useState(false)
  const { key, setKey, hasKey } = useGroqKey()
  const [draft, setDraft] = useState(key)
  const [revealed, setRevealed] = useState(false)

  useEffect(() => {
    if (open) setDraft(key)
  }, [open, key])

  const save = () => {
    setKey(draft)
    setOpen(false)
  }

  const clear = () => {
    setKey('')
    setDraft('')
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          className ??
          'inline-flex items-center gap-1 text-xs uppercase tracking-widest font-semibold px-3 py-1.5 rounded border border-slate-700 text-slate-300 hover:text-white hover:border-amber-500 transition-colors'
        }
        aria-label="Open settings — Groq API key"
        title={hasKey ? 'Groq key configured' : 'Add a Groq API key to enable AI Enhance'}
      >
        {children ?? (
          <>
            <span aria-hidden>{hasKey ? '🔑' : '⚙'}</span>
            <span>{hasKey ? 'Key set' : 'Settings'}</span>
          </>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="ca-settings-title"
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false)
          }}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div className="relative w-full max-w-lg rounded-xl border border-slate-700 bg-slate-900 p-6 shadow-2xl">
            <h2 id="ca-settings-title" className="text-xl font-semibold text-white mb-1">
              Groq API Key
            </h2>
            <p className="text-sm text-slate-400 mb-5 leading-relaxed">
              The PWA works fully without an AI key — all core logic runs in your browser. Add a free
              Groq API key to unlock the optional <strong className="text-amber-400">AI Enhance</strong> button.
              Your key is stored in this browser&apos;s LocalStorage and never sent anywhere except api.groq.com.
            </p>

            <label className="block text-xs uppercase tracking-widest font-semibold text-amber-400 mb-2">
              Your Groq key
            </label>
            <div className="flex gap-2 mb-4">
              <input
                type={revealed ? 'text' : 'password'}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="gsk_..."
                spellCheck={false}
                autoComplete="off"
                className="flex-1 rounded-md border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500"
              />
              <button
                type="button"
                onClick={() => setRevealed((v) => !v)}
                className="text-xs uppercase tracking-widest font-semibold px-3 rounded border border-slate-700 text-slate-300 hover:text-white"
              >
                {revealed ? 'Hide' : 'Show'}
              </button>
            </div>

            <p className="text-xs text-slate-500 mb-6">
              Get a free key at{' '}
              <a
                href="https://console.groq.com/keys"
                target="_blank"
                rel="noopener noreferrer"
                className="text-amber-400 underline hover:text-amber-300"
              >
                console.groq.com/keys
              </a>{' '}
              · Free tier covers ~14,400 requests/day on llama-3.3-70b-versatile.
            </p>

            <div className="flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={clear}
                className="text-xs text-slate-500 hover:text-red-400 transition-colors"
              >
                Remove saved key
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="text-sm font-medium px-4 py-2 rounded-md border border-slate-700 text-slate-300 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={save}
                  className="text-sm font-semibold px-4 py-2 rounded-md bg-amber-500 text-slate-900 hover:bg-amber-400"
                >
                  Save key
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
